import { useQuery } from '@tanstack/react-query';

import map from 'lodash/map';

import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { getRequest, serialize } from 'src/utils/request';

import type { RequestData } from 'src/types/requestData';

const mapTargetShiftOption = (
  shift: UCM.TargetShiftOptionRawType,
  shiftId: string,
): UCM.TargetShiftOptionType => {
  const { shifts, uuid, title, project, transferable } = shift;
  return {
    id: shiftId,
    uuid,
    title,
    project,
    transferable,
    shifts: map(shifts, (shift) => ({
      id: shift.id,
      uuid: shift.uuid,
      endsAt: shift.ends_at,
      selected: shift.selected,
      startsAt: shift.starts_at,
      availableSlots: shift.available_slots,
    })),
  };
};

type TargetShiftOptionParams = {
  jobId: string;
  standbyJobUuid: string;
  selectedUserUuids: Array<string>;
};

const getTargetShiftOptions = async ({
  jobId,
  standbyJobUuid,
  selectedUserUuids,
}: TargetShiftOptionParams) => {
  const query = serialize(selectedUserUuids, 'user_uuids');

  const {
    data: { data },
  } = await getRequest<RequestData<UCM.TargetShiftOptionRawType>>(
    `${API.GET.getTargetShiftOptions(standbyJobUuid, jobId)}?${query}`,
  );

  return mapTargetShiftOption(data.attributes, data.id);
};

export const useGetTargetShiftOptions = (
  params: TargetShiftOptionParams,
  config: { enabled?: boolean } = { enabled: true },
) => {
  const { jobId, standbyJobUuid, selectedUserUuids: uuids } = params;

  return useQuery({
    queryKey: [QUERY_KEYS.TARGET_SHIFT_OPTIONS, jobId, standbyJobUuid, uuids],
    queryFn: () => getTargetShiftOptions(params),
    staleTime: 1000 * 60 * 5,
    retry: false, // Because if not found BE returns 404 and because of this react-query thinks that it's retryable. So, I had to pass this attribute to tell react-query to not retry.
    ...config,
  });
};
